import React, { useCallback, useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { AnimatePresence, motion } from 'framer-motion'
import { Package, Store, Truck, X } from 'lucide-react'

export const featureTourStorageKey = (tourId) => `tour_${tourId}`

export function hasSeenFeatureTour(tourId) {
  if (!tourId || typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(featureTourStorageKey(tourId)) === 'seen'
  } catch {
    return false
  }
}

export function markFeatureTourSeen(tourId) {
  if (!tourId || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(featureTourStorageKey(tourId), 'seen')
  } catch {
    /* ignore quota / private mode */
  }
}

function SowKpiPreview() {
  const cards = [
    {
      label: 'Assigned sellers',
      value: '23',
      hint: 'Assigned to you',
      icon: Store,
      accent: 'bg-violet-50 text-violet-700',
    },
    {
      label: 'Created orders',
      value: '30,390',
      hint: 'Selected date range',
      icon: Package,
      accent: 'bg-sky-50 text-sky-700',
    },
    {
      label: 'Shipped orders',
      value: '28,250',
      hint: 'Ship rate 93%',
      icon: Truck,
      accent: 'bg-emerald-50 text-emerald-700',
    },
  ]

  return (
    <div className="grid grid-cols-3 gap-2">
      {cards.map((card) => {
        const Icon = card.icon
        return (
          <div
            key={card.label}
            className="rounded-lg border border-slate-200/90 bg-white p-2.5 shadow-[0_4px_12px_rgba(0,0,0,0.04)]"
          >
            <div className="flex items-start justify-between gap-1.5">
              <div className="min-w-0">
                <p className="truncate text-[8px] font-semibold uppercase tracking-wide text-slate-500">
                  {card.label}
                </p>
                <p className="mt-1 text-sm font-bold tabular-nums text-slate-900">
                  {card.value}
                </p>
                <p className="mt-0.5 truncate text-[9px] text-slate-500">
                  {card.hint}
                </p>
              </div>
              <span
                className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${card.accent}`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Shiprocket-style feature announcement / coachmark popup.
 * Dismissed tours persist in localStorage under `tour_{tourId}`.
 */
const FeatureAnnouncementModal = ({
  tourId,
  title,
  description,
  badgeText = 'New',
  previewImageUrl,
  previewAlt = 'Feature preview',
  buttonText = 'Got it',
  isOpen,
  onClose,
  closeOnBackdrop = false,
}) => {
  const titleId = useId()
  const descriptionId = useId()
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const seen = hasSeenFeatureTour(tourId)
    const parentAllows = isOpen !== false
    setVisible(Boolean(tourId) && parentAllows && !seen)
    setReady(true)
  }, [tourId, isOpen])

  const handleDismiss = useCallback(async () => {
    setVisible(false)
    markFeatureTourSeen(tourId)
    try {
      await onClose?.({ tourId, seen: true })
    } catch {
      /* parent API sync should not block dismiss */
    }
  }, [onClose, tourId])

  useEffect(() => {
    if (!visible) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') handleDismiss()
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [visible, handleDismiss])

  if (!ready || typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {visible ? (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeOnBackdrop ? handleDismiss : undefined}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            className="relative w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-[0_24px_80px_rgba(15,23,42,0.28)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleDismiss}
              className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-sm ring-1 ring-slate-200/80 transition-colors hover:bg-slate-50 hover:text-slate-800"
              aria-label="Close announcement"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>

            <div className="bg-slate-100 p-3 sm:p-3.5">
              <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 p-3">
                {previewImageUrl ? (
                  <img
                    src={previewImageUrl}
                    alt={previewAlt}
                    className="h-[168px] w-full rounded-lg object-cover object-top sm:h-[188px]"
                  />
                ) : (
                  <SowKpiPreview />
                )}
              </div>
            </div>

            <div className="px-5 pb-5 pt-4 sm:px-6 sm:pb-6">
              {badgeText ? (
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200/80">
                  {badgeText}
                </span>
              ) : null}
              <h2
                id={titleId}
                className="mt-2.5 text-lg font-semibold tracking-tight text-slate-900 sm:text-xl"
              >
                {title}
              </h2>
              <p
                id={descriptionId}
                className="mt-1.5 text-sm leading-relaxed text-slate-500"
              >
                {description}
              </p>
              <button
                type="button"
                onClick={handleDismiss}
                className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-xl bg-[#263f97] px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1e327a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#263f97]/40 focus-visible:ring-offset-2"
              >
                {buttonText}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  )
}

FeatureAnnouncementModal.propTypes = {
  tourId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  badgeText: PropTypes.string,
  previewImageUrl: PropTypes.string,
  previewAlt: PropTypes.string,
  buttonText: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  closeOnBackdrop: PropTypes.bool,
}

export default FeatureAnnouncementModal
